import React, { useEffect, useRef } from 'react';
import { RiBuildingLine, RiCheckLine, RiTrophyLine, RiMedalLine, RiPencilLine, RiDeleteBinLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import { TrustScore } from '../../../trust-score';
import { formatPrice, trunc2 } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation } from '@/features/tails/utils';
/**
 * AircraftCard Component
 * Displays an individual aircraft option with auto-computed pricing (read-only)
 *
 * @param {Object} props
 * @param {Object} props.aircraft - Aircraft data object
 * @param {number} props.passengerCount - Number of passengers for segment tax calculation
 * @param {string} props.flightType - Type of flight: 'one-way', 'round-trip', or 'multi-leg'
 * @param {number} props.flightsLength - Number of flights (legs) for multi-leg trips
 * @param {Function} props.onPriceChange - Callback when prices are computed
 * @param {Function} props.onEditPricing - Callback when Edit Pricing button is clicked
 */
const AircraftCard = ({ aircraft, passengerCount = 6, flightType = 'one-way', flightsLength = 1, onPriceChange, onEditPricing, onRemove }) => {
  const hasInitialized = useRef(false);
  // Initialize with actual prop values so valuesChanged is false on remount
  const prevValuesRef = useRef({ passengerCount, flightType, flightsLength });

  // Tax rate constants
  const BROKER_MARGIN_RATE = 0.15; // 15% Brokerage Margin
  const FET_RATE = 0.075; // 7.5% Federal Excise Tax
  const PLATFORM_FEE_RATE = 0.015; // 1.5% TrustJet Fee
  const CREDIT_CARD_FEE_RATE = 0.03; // 3% Credit Card Fee
  const SEGMENT_FEE_PER_PAX = 5.20; // $5.20 per passenger

  // Calculate leg multiplier based on flight type
  // one-way: 1, round-trip: 2, multi-leg: number of flights
  const getLegMultiplier = () => {
    if (flightType === 'round-trip') return 2;
    if (flightType === 'multi-leg') return flightsLength;
    return 1; // one-way
  };
  const legMultiplier = getLegMultiplier();

  /**
   * Forward calculation: Calculate taxes and total from operator_cost and broker_markup
   */
  const calculateForward = (operatorCost, brokerMarkup, segmentTax) => {
    const taxableBase = operatorCost + brokerMarkup;
    const platformFee = taxableBase * PLATFORM_FEE_RATE;
    const federalExciseTax = (taxableBase + platformFee) * FET_RATE;
    const totalBeforeCCFee = taxableBase + federalExciseTax + platformFee + segmentTax;
    const creditCardFee = totalBeforeCCFee * CREDIT_CARD_FEE_RATE;
    const totalPrice = totalBeforeCCFee + creditCardFee;

    return {
      federalExciseTax: trunc2(federalExciseTax).toFixed(2),
      platformFee: trunc2(platformFee).toFixed(2),
      creditCardFee: trunc2(creditCardFee).toFixed(2),
      totalPrice: trunc2(totalPrice).toFixed(2),
      brokerMarkup: trunc2(brokerMarkup).toFixed(2),
      segmentTax: trunc2(segmentTax).toFixed(2)
    };
  };

  // Initialize calculated values on mount/when aircraft data is available
  // Also recalculate when passengerCount, flightType, or flightsLength change
  useEffect(() => {
    if (!aircraft || !onPriceChange) return;

    // Check if aircraft already has edited values (to preserve edits on remount)
    const hasExistingValues = aircraft.broker_markup !== null &&
                              aircraft.broker_markup !== undefined &&
                              aircraft.broker_markup !== '';

    // Check if segment-tax-affecting values changed
    const valuesChanged =
      prevValuesRef.current.passengerCount !== passengerCount ||
      prevValuesRef.current.flightType !== flightType ||
      prevValuesRef.current.flightsLength !== flightsLength;

    // Skip if values already exist and segment-tax-affecting values haven't changed
    if (hasExistingValues && !valuesChanged) {
      hasInitialized.current = true;
      prevValuesRef.current = { passengerCount, flightType, flightsLength };
      return;
    }

    // Skip if already initialized and values haven't changed
    if (hasInitialized.current && !valuesChanged) return;

    const optionId = aircraft.bid_option_id || aircraft.aircraft_id;

    const operatorCost = parseFloat(aircraft.total_price) || 0;

    // Auto-calculate segment tax as passengerCount * $5.20 * legMultiplier
    const segment = (passengerCount * SEGMENT_FEE_PER_PAX) * legMultiplier;

    // Auto-calculate broker margin as 15% of operator cost
    const brokerMark = operatorCost * BROKER_MARGIN_RATE;

    // Calculate and save initial values using forward calculation
    const { federalExciseTax, platformFee, creditCardFee, totalPrice, brokerMarkup, segmentTax } = calculateForward(operatorCost, brokerMark, segment);

    onPriceChange(optionId, brokerMarkup, 'broker_markup');
    onPriceChange(optionId, segmentTax, 'segment_tax');
    onPriceChange(optionId, federalExciseTax, 'federal_excise_tax');
    onPriceChange(optionId, platformFee, 'platform_fee');
    onPriceChange(optionId, creditCardFee, 'credit_card_fee');
    onPriceChange(optionId, totalPrice, 'final_price');

    // Update refs
    hasInitialized.current = true;
    prevValuesRef.current = { passengerCount, flightType, flightsLength };
  }, [aircraft, onPriceChange, passengerCount, flightType, flightsLength, legMultiplier]);

  // Get display values for price fields
  const getDisplayValue = (field, fallbackCalculation) => {
    const aircraftValue = aircraft[field];
    // Check for valid value (not null, undefined, or empty string)
    if (aircraftValue !== null && aircraftValue !== undefined && aircraftValue !== '') {
      const parsed = parseFloat(aircraftValue);
      if (!isNaN(parsed) && parsed >= 0) {
        return parsed;
      }
    }
    return fallbackCalculation ? fallbackCalculation() : 0;
  };

  // Handle null aircraft gracefully
  if (!aircraft) {
    return (
      <article
        data-testid="aircraft-card"
        className="border rounded-lg shadow-sm border-gray-200 p-4"
        role="article"
        aria-label="Aircraft card"
      >
        <p className="text-gray-500">Aircraft data unavailable</p>
      </article>
    );
  }

  return (
    <article
      data-testid="aircraft-card"
      role="article"
      aria-label={`${aircraft.aircraft?.tail_number} aircraft card`}
      className={cn(
        'relative border rounded-xl transition-all p-4 h-full',
        'border-gray-200 bg-white'
      )}
    >
      {/* Header with TrustScore and Selected Badge */}
      <div className="flex items-start justify-between">
        {/* Aircraft Name and Type */}
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 mb-1">
          {getAircraftModelName(aircraft.aircraft)}
          </h3>
          <p className="text-xs text-gray-500">{aircraft.aircraft?.tail_number}</p>

          {/* Location/Operator Info */}
          <div className="flex items-top-center gap-1 text-xs text-gray-600 mb-3">
            <RiBuildingLine className="w-3.5 h-3.5" />
            {aircraft.aircraft?.operator &&
            <span>{getOperatorWithLocation(aircraft.aircraft?.operator)}</span>
            }
          </div>

        </div>

        {/* TrustScore Badge */}
        <div className="ml-2">
          <TrustScore score={aircraft.aircraft?.operator?.rating_tiers} numericScore={aircraft.aircraft?.operator?.trust_score || 0} size="sm" />
        </div>

      </div>

 
      {/* Feature Badges */}
      <div className="flex flex-wrap gap-2 mb-4">

        {aircraft.aircraft?.operator?.argus_rating && aircraft.aircraft?.operator?.argus_rating!="No" &&
          <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
            <RiTrophyLine className="h-4 w-4 text-gray-600"/> {aircraft.aircraft?.operator?.argus_rating}
          </span>}
          
          {aircraft.aircraft?.operator?.wyvern_rating && aircraft.aircraft?.operator?.wyvern_rating!="No" &&
          <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
            <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
          </span>
          }

          {aircraft.aircraft_features?.no_aog &&
          <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
            <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
          </span>
          }

      </div>

      {/* Price Breakdown */}
        <div className="space-y-2">
          {/* Operator Cost */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Operator Cost</span>
            <span className="text-sm text-gray-900">
              {formatPrice(getDisplayValue('operator_cost'))}
            </span>
          </div>

          {/* Brokerage Margin */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Brokerage Margin (15%)</span>
            <span className="text-sm text-gray-900">
              {formatPrice(getDisplayValue('broker_markup'))}
            </span>
          </div>

          {/* Federal Excise Tax */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Federal Excise Tax (7.5%)</span>
            <span className="text-sm text-gray-900">
              {formatPrice(getDisplayValue('federal_excise_tax'))}
            </span>
          </div>

          {/* International or Segment Tax */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">International or Segment Tax</span>
            <span className="text-sm text-gray-900">
              {formatPrice(getDisplayValue('segment_tax'))}
            </span>
          </div>

          {/* TrustJet Fee */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">TrustJet Fee (1.5%)</span>
            <span className="text-sm text-gray-900">
              {formatPrice(getDisplayValue('platform_fee'))}
            </span>
          </div>

          {/* Total Cost */}
          <div className="flex justify-between items-center border-t border-gray-200 mt-3 pt-2">
            <span className="text-md font-medium text-gray-900">Total Cost</span>
            <span className="text-md font-semibold text-gray-900">
              {formatPrice(getDisplayValue('total_price'))}
            </span>
          </div>

          {/* Credit Card Fee */}
          <div className="flex justify-between items-center mt-3 pb-3">
            {/* <span className="text-sm text-gray-500 italic">Credit Card Fee (3%)</span> */}
            <span className="text-sm text-gray-500">
              {/* {formatPrice(getDisplayValue('credit_card_fee', () => {
                const operatorCost = parseFloat(aircraft.total_price) || 0;
                const brokerMarkup = trunc2(operatorCost * BROKER_MARGIN_RATE);
                const subtotal = operatorCost + brokerMarkup;
                const platformFee = trunc2(subtotal * PLATFORM_FEE_RATE);
                const federalExciseTax = trunc2(subtotal * FET_RATE);
                const segmentTax = trunc2(passengerCount * SEGMENT_FEE_PER_PAX * legMultiplier);
                const totalBeforeCCFee = subtotal + platformFee + federalExciseTax + segmentTax;
                return trunc2(totalBeforeCCFee * CREDIT_CARD_FEE_RATE);
              }))} */}
              Credit card payments will incur an additional 3% on top of the total cost.
            </span>
          </div>

          {/* Total Cost with Credit Card Fee */}
          {/* <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-500">Total Cost (includes card fee)</span>
            <span className="text-sm font-semibold text-gray-500">
              {formatPrice((() => {
                const operatorCost = getDisplayValue('total_price', () => parseFloat(aircraft.total_price) || 0);
                const brokerMarkup = getDisplayValue('broker_markup', () => trunc2(operatorCost * BROKER_MARGIN_RATE));
                const subtotal = operatorCost + brokerMarkup;
                const platformFee = getDisplayValue('platform_fee', () => trunc2(subtotal * PLATFORM_FEE_RATE));
                const federalExciseTax = getDisplayValue('federal_excise_tax', () => trunc2(subtotal * FET_RATE));
                const segmentTax = getDisplayValue('segment_tax', () => trunc2(passengerCount * SEGMENT_FEE_PER_PAX * legMultiplier));
                const totalBeforeCCFee = subtotal + platformFee + federalExciseTax + segmentTax;
                const creditCardFee = getDisplayValue('credit_card_fee', () => trunc2(totalBeforeCCFee * CREDIT_CARD_FEE_RATE));
                return trunc2(totalBeforeCCFee + creditCardFee);
              })())}
            </span>
          </div> */}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-5">
            <button
              type="button"
              onClick={() => onEditPricing?.(aircraft)}
              className="flex-1 px-4 py-2.5 border border-primary-500 rounded-full text-sm font-medium text-primary-500 transition-colors flex items-center justify-center gap-2"
            >
              <RiPencilLine className="w-4 h-4" />
              Edit Pricing
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(aircraft.bid_option_id)}
                className="flex-1 px-4 py-2.5 border border-red-300 rounded-full text-sm font-medium text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <RiDeleteBinLine className="w-4 h-4" />
                Remove
              </button>
            )}
          </div>
        </div>

    </article>
  );
};

export default AircraftCard;
